// Browser detection and Safari-specific utilities
export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent);
  const isIOS = /iphone|ipad|ipod/.test(userAgent);
  const isMobileSafari = isSafari && isIOS;
  
  return {
    isSafari,
    isIOS,
    isMobileSafari,
    isWebKit: /webkit/.test(userAgent),
    version: isSafari ? getSafariVersion(userAgent) : null
  };
};

const getSafariVersion = (userAgent: string): string | null => {
  const match = userAgent.match(/version\/(\d+(\.\d+)*)/);
  return match ? match[1] : null;
};

// Safari-specific optimizations for fetch operations
export const createSafariOptimizedFetch = () => {
  const browser = detectBrowser();
  
  return {
    // Enhanced fetch with Safari-specific timeouts and error handling
    fetchWithSafariSupport: async (url: string, options: RequestInit & { 
      timeout?: number;
      retryCount?: number;
      retryDelay?: number;
    } = {}) => {
      const {
        timeout = browser.isSafari ? 60000 : 30000, // Longer timeout for Safari
        retryCount = browser.isSafari ? 3 : 1,
        retryDelay = 2000,
        ...fetchOptions
      } = options;

      let lastError: Error | null = null;

      for (let attempt = 0; attempt < retryCount; attempt++) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          const response = await fetch(url, {
            ...fetchOptions,
            signal: controller.signal,
            // Safari-specific headers
            headers: {
              ...fetchOptions.headers,
              ...(browser.isSafari && {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              })
            }
          });

          clearTimeout(timeoutId);
          return response;
        } catch (error) {
          lastError = error as Error;
          console.warn(`Fetch attempt ${attempt + 1} failed:`, error);
          
          // Don't retry on certain errors
          if (error instanceof Error && 
              (error.name === 'AbortError' || 
               error.message.includes('network'))) {
            if (attempt < retryCount - 1) {
              await new Promise(resolve => setTimeout(resolve, retryDelay));
              continue;
            }
          }
          throw error;
        }
      }

      throw lastError || new Error('All fetch attempts failed');
    }
  };
};

// Safari-specific file handling
export const createSafariFileHandler = () => {
  const browser = detectBrowser();
  
  return {
    // Enhanced FormData creation for Safari
    createSafariFormData: (file: File): FormData => {
      const formData = new FormData();
      
      if (browser.isSafari) {
        // Safari sometimes has issues with File objects, so we create a Blob
        const blob = new Blob([file], { type: file.type || 'application/pdf' });
        formData.append('file', blob, file.name);
      } else {
        formData.append('file', file);
      }
      
      return formData;
    },

    // Enhanced URL object creation with proper cleanup
    createSafariSafeObjectURL: (file: File): { url: string; cleanup: () => void } => {
      const url = URL.createObjectURL(file);
      let isRevoked = false;

      const cleanup = () => {
        if (!isRevoked) {
          URL.revokeObjectURL(url);
          isRevoked = true;
        }
      };

      // Auto-cleanup for Safari after a reasonable time to prevent memory leaks
      if (browser.isSafari) {
        setTimeout(cleanup, 5 * 60 * 1000); // 5 minutes
      }

      return { url, cleanup };
    }
  };
};

// Safari-specific polling optimization
export const createSafariPolling = () => {
  const browser = detectBrowser();
  
  return {
    pollWithSafariSupport: async <T>(
      pollFn: () => Promise<{ status: string; data?: T; error?: string }>,
      options: {
        maxAttempts?: number;
        baseInterval?: number;
        maxInterval?: number;
        exponentialBackoff?: boolean;
        onProgress?: (attempt: number, status: string) => void;
      } = {}
    ): Promise<T> => {
      const {
        maxAttempts = browser.isSafari ? 150 : 100, // More attempts for Safari
        baseInterval = browser.isSafari ? 3000 : 2000, // Longer intervals for Safari
        maxInterval = 10000,
        exponentialBackoff = false,
        onProgress
      } = options;

      let attempt = 0;
      let currentInterval = baseInterval;

      const poll = async (): Promise<T> => {
        if (attempt >= maxAttempts) {
          throw new Error(`Polling timeout after ${maxAttempts} attempts`);
        }

        try {
          const result = await pollFn();
          onProgress?.(attempt, result.status);

          if (result.status === 'completed' && result.data) {
            return result.data;
          } else if (result.status === 'failed') {
            throw new Error(result.error || 'Processing failed');
          }

          // Continue polling
          attempt++;
          
          if (exponentialBackoff) {
            currentInterval = Math.min(currentInterval * 1.2, maxInterval);
          }

          await new Promise(resolve => setTimeout(resolve, currentInterval));
          return poll();
        } catch (error) {
          if (error instanceof Error && error.message.includes('timeout')) {
            throw error;
          }
          
          // For network errors, wait a bit longer before retrying
          await new Promise(resolve => setTimeout(resolve, currentInterval * 2));
          attempt++;
          return poll();
        }
      };

      return poll();
    }
  };
};

// Safari visibility API handling
export const createSafariVisibilityHandler = () => {
  const browser = detectBrowser();
  
  return {
    // Check if the page is visible (Safari handles this differently)
    isPageVisible: (): boolean => {
      if (browser.isSafari) {
        // Safari sometimes doesn't properly support document.visibilityState
        return !document.hidden;
      }
      return document.visibilityState === 'visible';
    },

    // Enhanced visibility change listener for Safari
    onVisibilityChange: (callback: (isVisible: boolean) => void): (() => void) => {
      const handler = () => {
        const isVisible = browser.isSafari ? !document.hidden : document.visibilityState === 'visible';
        callback(isVisible);
      };

      // Safari uses different events
      if (browser.isSafari) {
        document.addEventListener('webkitvisibilitychange', handler);
        return () => document.removeEventListener('webkitvisibilitychange', handler);
      } else {
        document.addEventListener('visibilitychange', handler);
        return () => document.removeEventListener('visibilitychange', handler);
      }
    }
  };
}; 